/**
 * The AWS event-stream framing Transcribe's WebSocket speaks, both ways.
 *
 * This is not JSON over a socket. Every message is a binary frame: a
 * twelve byte prelude (total length, headers length, CRC32 of those eight
 * bytes), then typed headers, then the payload, then a CRC32 of everything
 * before it. Transcribe closes the connection on a frame it cannot parse
 * and says little about why, so both CRCs are computed properly rather
 * than stubbed - a wrong checksum looks exactly like a network fault.
 *
 * Only the two header types this protocol uses are implemented: string (7)
 * for the event names, and nothing else. Anything wider would be code with
 * no caller.
 */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const byte of bytes) {
    // The mask keeps the index inside the table, so the lookup cannot miss.
    c = (CRC_TABLE[(c ^ byte) & 0xff] as number) ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

const HEADER_STRING = 7;

function stringHeader(name: string, value: string): Uint8Array {
  const n = new TextEncoder().encode(name);
  const v = new TextEncoder().encode(value);
  const out = new Uint8Array(1 + n.length + 1 + 2 + v.length);
  const view = new DataView(out.buffer);
  let o = 0;
  out[o++] = n.length;
  out.set(n, o);
  o += n.length;
  out[o++] = HEADER_STRING;
  view.setUint16(o, v.length, false);
  o += 2;
  out.set(v, o);
  return out;
}

/** Wrap raw 16-bit PCM as one AudioEvent frame. */
export function encodeAudioEvent(pcm: Uint8Array): Uint8Array {
  const headers = [
    stringHeader(':message-type', 'event'),
    stringHeader(':event-type', 'AudioEvent'),
    stringHeader(':content-type', 'application/octet-stream'),
  ];
  const headersLength = headers.reduce((n, h) => n + h.length, 0);
  const total = 16 + headersLength + pcm.length;

  const frame = new Uint8Array(total);
  const view = new DataView(frame.buffer);
  view.setUint32(0, total, false);
  view.setUint32(4, headersLength, false);
  view.setUint32(8, crc32(frame.subarray(0, 8)), false);

  let o = 12;
  for (const h of headers) {
    frame.set(h, o);
    o += h.length;
  }
  frame.set(pcm, o);
  view.setUint32(total - 4, crc32(frame.subarray(0, total - 4)), false);
  return frame;
}

export interface TranscriptFrame {
  /** Absent on frames that carry no transcript, such as keep-alives. */
  text?: string;
  /** Transcribe revises a partial until it is sure; only a final is worth acting on. */
  partial?: boolean;
  /** An exception frame: the message is Transcribe's, not ours. */
  error?: string;
}

/** Read one frame from Transcribe. Never throws: a frame we cannot parse
 *  must not take the microphone down mid-sentence. */
export function decodeFrame(buffer: ArrayBuffer): TranscriptFrame {
  try {
    const bytes = new Uint8Array(buffer);
    const view = new DataView(buffer);
    const headersLength = view.getUint32(4, false);
    const payload = new TextDecoder().decode(bytes.subarray(12 + headersLength, bytes.length - 4));
    const headerBlock = new TextDecoder().decode(bytes.subarray(12, 12 + headersLength));

    if (headerBlock.includes('exception')) {
      const body = JSON.parse(payload) as { Message?: string; message?: string };
      return { error: body.Message ?? body.message ?? 'Transcribe rejected the stream' };
    }

    const body = JSON.parse(payload) as {
      Transcript?: { Results?: { Alternatives?: { Transcript?: string }[]; IsPartial?: boolean }[] };
    };
    const result = body.Transcript?.Results?.[0];
    if (!result) return {};
    return {
      text: result.Alternatives?.[0]?.Transcript ?? '',
      partial: result.IsPartial !== false,
    };
  } catch {
    return {};
  }
}
